import React, { useEffect, useState } from "react";

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import ls from "localstorage-slim";
import { useTranslation } from "react-i18next";
import { Route, Routes, useLocation, useNavigate } from "react-router-dom";

import MultiStoreyBuildings from "@/pages/MultiStoreyBuildings.page";
import Project from "@/pages/Project";

import CookieConsentComponent from "./components/CookieConsent.component";
import ErrorComponent from "./components/Error.component";
import FooterComponent from "./components/Footer.component";
import useGaTracker from "./components/GA.component";
import MetaPixelComponent from "./components/MetaPixel.component";
import NavbarComponent from "./components/Navbar.component";
import PinCodeComponent from "./components/PinCode.component";
import RedirectComponent from "./components/RedirectComponent";
import ReserveSuccess from "./components/reserve/ReserveSuccess.component";
import UnsubscribeComponent from "./components/Unsubscribe.component";
import useCart from "./hooks/useCart";
import About from "./pages/About.page";
import Awards from "./pages/Awards.page";
import Blog from "./pages/Blog.page";
import Blogs from "./pages/Blogs.page";
import Cart from "./pages/Cart.page";
import Checkout from "./pages/Checkout.page";
import Contact from "./pages/Contact.page";
import Events from "./pages/Events.page";
import Gallery from "./pages/Gallery.page";
import Home from "./pages/Home.page";
import MediaCoverage from "./pages/MediaCoverage.page";
import Model from "./pages/Model.page";
import Models from "./pages/Models.page";
import Partnership from "./pages/Partnership.page";
import PaymentSuccess from "./pages/PaymentSuccess.page";
import PrivacyPolicy from "./pages/PrivacyPolicy.page";
import Product from "./pages/Product.page";
import Projects from "./pages/Projects.page";
import RefundPolicy from "./pages/RefundPolicy.page";
import ReservationAgreement from "./pages/ReservationAgreement.page";
import Shop from "./pages/Shop.page";
import Technology from "./pages/Technology.page";
import TermsOfService from "./pages/TermsOfService.page";
import VCard from "./pages/VCard.page";
import { getCurrentLang } from "./utils/Local";

const queryClient = new QueryClient();

const App = (): React.JSX.Element => {
  const { i18n } = useTranslation();
  const { cart } = useCart();
  const [authenticated, setAuthenticated] = useState(false);
  const [totalFailedAuth, setTotalFailedAuth] = useState(0);
  const [pin, setPin] = useState("");
  const language = getCurrentLang();
  const navigate = useNavigate();

  useEffect(() => {
    let preferredLang;

    const storedLang = ls.get("userLang");
    const userLang = navigator.language;

    if (storedLang) {
      preferredLang = storedLang === "fr" ? "fr" : "en";
    } else if (userLang) {
      preferredLang = userLang.startsWith("fr") ? "fr" : "en";
    }

    if (preferredLang) {
      const path = window.location.pathname;
      if (path === "/" && preferredLang === "fr") {
        navigate("/fr/");
      } else if (
        (path === "/fr/" || path === "/fr") &&
        preferredLang === "en"
      ) {
        navigate("/");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    i18n
      .changeLanguage(language)
      .then((r) => {
        ls.set("userLang", language);
      })
      .catch((e) => {
        console.error(e);
      });
  }, [i18n, language]);

  // Scroll to top
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  // Disable right-click
  useEffect(() => {
    function handleContextMenu(e: any) {
      e.preventDefault();
    }

    const rootElement = document.getElementById("root") as HTMLElement;
    rootElement.addEventListener("contextmenu", handleContextMenu);

    return () => {
      rootElement.removeEventListener("contextmenu", handleContextMenu);
    };
  }, []);

  // Cart
  useEffect(() => {
    ls.set("cart", cart, { ttl: 432000, encrypt: true });
  }, [cart]);

  const handlePinComplete = (completedPin: string) => {
    if (completedPin === pin) {
      return;
    }

    setPin(completedPin);

    if (completedPin === import.meta.env.VITE_PIN_CODE) {
      setAuthenticated(true);
    } else {
      setTotalFailedAuth((prev) => prev + 1);
    }
  };

  const innerRoutes = (
    <Route>
      <Route index element={<Home />} />
      <Route element={<Home />} path="/" />
      <Route element={<Home />} path="/fr/" />

      <Route element={<Models />} path="/models/overview" />
      <Route element={<Models />} path="/fr/modeles/overview" />

      <Route element={<Model />} path="/models/:model" />
      <Route element={<Model />} path="/fr/modeles/:model" />

      {/* <Route path={`/how-it-works`} element={<HowItWorks/>}/> */}
      {/* <Route path={`/fr/comment-ca-marche`} element={<HowItWorks/>}/> */}

      <Route element={<Technology />} path="/the-technology" />
      <Route element={<Technology />} path="/fr/la-technologie" />

      <Route element={<About />} path="/our-story" />
      <Route element={<About />} path="/fr/notre-histoire" />

      <Route element={<About />} path="/who-we-are" />
      <Route element={<About />} path="/fr/qui-nous-sommes" />

      {/* <Route path={`/faq`} element={<Faq/>}/> */}
      {/* <Route path={`/fr/faq`} element={<Faq/>}/> */}

      <Route element={<Contact />} path="/contact-us" />
      <Route element={<Contact />} path="/fr/contactez-nous" />

      <Route element={<PrivacyPolicy />} path="/privacy-policy" />
      <Route
        element={<PrivacyPolicy />}
        path="/fr/politique-de-confidentialite"
      />

      <Route element={<RefundPolicy />} path="/refund-policy" />
      <Route element={<RefundPolicy />} path="/fr/politique-de-remboursement" />

      <Route element={<TermsOfService />} path="/terms-of-service" />
      <Route element={<TermsOfService />} path="/fr/conditions-de-service" />

      <Route element={<ReservationAgreement />} path="/reservation-agreement" />
      <Route
        element={<ReservationAgreement />}
        path="/fr/contrat-de-reservation"
      />

      <Route element={<VCard />} path="/vcard/:contact" />
      <Route element={<VCard />} path="/fr/vcard/:contact" />

      <Route element={<MediaCoverage />} path="/media-coverage" />
      <Route element={<MediaCoverage />} path="/fr/couverture-mediatique" />

      <Route element={<Gallery />} path="/gallery" />
      <Route element={<Gallery />} path="/fr/galerie" />

      <Route element={<Product />} path="/shop/:product" />
      <Route element={<Product />} path="/fr/commandez/:product" />

      <Route element={<Blog />} path="/blog/:blog" />
      <Route element={<Blog />} path="/fr/blog/:blog" />

      <Route element={<Blogs />} path="/blog" />
      <Route element={<Blogs />} path="/fr/blog" />

      <Route element={<Shop />} path="/shop" />
      <Route element={<Shop />} path="/fr/commandez" />

      <Route element={<Cart />} path="/cart" />
      <Route element={<Cart />} path="/fr/panier" />

      <Route element={<Checkout />} path="/checkout" />
      <Route element={<Checkout />} path="/fr/caisse" />

      <Route element={<PaymentSuccess />} path="/success" />
      <Route element={<PaymentSuccess />} path="/fr/succes" />

      <Route element={<ReserveSuccess />} path="/success-reserve" />
      <Route element={<ReserveSuccess />} path="/fr/success-reserve" />

      <Route element={<ErrorComponent />} path="/error" />
      <Route element={<ErrorComponent />} path="/fr/erreur" />

      <Route
        element={<RedirectComponent type="sac" />}
        path="/schedule-a-call"
      />
      <Route
        element={<RedirectComponent type="sac" />}
        path="/fr/planifiez-un-appel"
      />

      <Route element={<RedirectComponent type="psd" />} path="/pay" />
      <Route element={<RedirectComponent type="psd" />} path="/fr/payer" />

      <Route element={<UnsubscribeComponent />} path="/manage-preferences" />
      <Route element={<UnsubscribeComponent />} path="/fr/gerer-preference" />

      <Route element={<Projects />} path="/projects" />
      <Route element={<Projects />} path="/fr/projets" />
      <Route element={<Project />} path="/projects/:project" />
      <Route element={<Project />} path="/fr/projets/:project" />

      <Route element={<Events />} path="/events" />
      <Route element={<Events />} path="/fr/evenements" />

      <Route element={<Awards />} path="/awards" />
      <Route element={<Awards />} path="/fr/prix" />

      <Route
        element={<MultiStoreyBuildings />}
        path="/multi-storey-buildings"
      />
      <Route
        element={<MultiStoreyBuildings />}
        path="/fr/batiments-multietages"
      />

      <Route element={<Partnership />} path="/community-partnerships" />
      <Route element={<Partnership />} path="/fr/partenariats-communautaires" />
    </Route>
  );

  // temp: plain layout | TODO: implement multiple layouts
  const displayDefaultLayout =
    !pathname.startsWith("/vcard") && !pathname.startsWith("/fr/vcard");

  useGaTracker();

  return (
    <React.Fragment>
      {totalFailedAuth > 4 ? (
        <React.Fragment>Error!</React.Fragment>
      ) : (
        <React.Fragment>
          {import.meta.env.VITE_ENV === "production" ||
          authenticated ||
          import.meta.env.MODE === "development" ? (
            <QueryClientProvider client={queryClient}>
              <MetaPixelComponent />
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  minHeight: "100vh",
                }}
              >
                {displayDefaultLayout ? <NavbarComponent /> : null}
                <div style={{ flex: "2 0", position: "relative" }}>
                  <Routes>
                    <Route path="/">{innerRoutes}</Route>
                    <Route element={<Home />} path="*" />
                  </Routes>
                </div>

                {displayDefaultLayout ? (
                  <React.Fragment>
                    <FooterComponent />
                    <CookieConsentComponent />
                    {/* <ScheduleCallModalComponent/> */}
                  </React.Fragment>
                ) : null}
              </div>
            </QueryClientProvider>
          ) : (
            <PinCodeComponent onComplete={handlePinComplete} />
          )}
        </React.Fragment>
      )}
    </React.Fragment>
  );
};

export default App;
